import { createFileRoute, Link } from "@tanstack/react-router";

export const Route = createFileRoute("/help/inviting-members")({
	component: InvitingMembers,
});

function InlineCode({ children }: { children: React.ReactNode }) {
	return (
		<code
			className="px-1.5 py-0.5 text-xs font-mono"
			style={{ backgroundColor: "rgba(234,40,4,0.08)", color: "#6B3FF2", border: "1px solid rgba(234,40,4,0.15)", borderLeft: "2px solid #6B3FF2", fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" }}
		>
			{children}
		</code>
	);
}

export function InvitingMembers() {
	return (
		<article className="space-y-10">
			<div className="space-y-2">
				<p className="text-[10px] font-bold uppercase tracking-[0.1em]" style={{ color: "#6B3FF2", fontFamily: "inherit" }}>Team Management</p>
				<h1
					className="text-4xl font-semibold tracking-tight"
					style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
				>
					Inviting members
				</h1>
				<p className="text-sm" style={{ color: "var(--km-on-surface-muted)" }}>
					Collaborate with your team inside a workspace.
				</p>
			</div>

			<div className="space-y-4 text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
				<p>
					Workspaces let you collaborate with your team. Each workspace has
					its own connections, posts, and settings.
				</p>
				<p>
					Tell your assistant things like:
				</p>
			</div>
			<div
				className="p-4 space-y-2 text-xs overflow-hidden"
				style={{
					background: "#0a0a0a",
					border: "1px solid rgba(255,255,255,0.1)",
					borderLeft: "3px solid #6B3FF2",
					fontFamily: "'Geist Mono', 'JetBrains Mono', monospace",
				}}
			>
				{[
					'"Invite alice@example.com to this workspace"',
					'"Add a new team member with email bob@example.com"',
					'"Who is in this workspace?"',
				].map((q) => (
					<div key={q} className="flex items-start gap-2" style={{ color: "#e1e4e8" }}>
						<span style={{ color: "#6B3FF2", flexShrink: 0 }}>›</span>
						<span>{q}</span>
					</div>
				))}
			</div>
			<div className="space-y-4 text-sm leading-relaxed" style={{ color: "var(--km-on-surface-muted)" }}>
				<p>
					The assistant uses <InlineCode>invite_team_member</InlineCode> to
					send the invitation and <InlineCode>list_members</InlineCode> to
					see who is currently in the workspace.
				</p>

				<h2
					className="text-base font-semibold pt-2"
					style={{ color: "var(--km-on-surface)", fontFamily: "'Geist', -apple-system, sans-serif" }}
				>
					Or use the dashboard
				</h2>
				<div className="space-y-2">
					{[
						<>Navigate to the <strong style={{ color: "var(--km-on-surface)" }}>Members</strong> tab in your dashboard.</>,
						<>Click <strong style={{ color: "var(--km-on-surface)" }}>Invite Member</strong> and enter the email address of the person you want to add.</>,
						<>Choose a role (see <Link to="/help/roles" className="transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60" style={{ color: "#6B3FF2" }} onMouseEnter={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }} onMouseLeave={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }} onFocus={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }} onBlur={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}>Roles and permissions</Link>).</>,
						<>The invitee receives an email with a join link. Once they accept, they appear in the members list.</>,
					].map((step, i) => (
						<div key={i} className="flex items-start gap-3 px-4 py-3" style={{ background: "var(--km-surface-low)", border: "1px solid rgba(255,255,255,0.08)", borderLeft: "3px solid #6B3FF2" }}>
							<span className="text-xs font-bold mt-0.5 shrink-0" style={{ color: "#6B3FF2", fontFamily: "'Geist Mono', 'JetBrains Mono', monospace" }}>#{String(i + 1).padStart(2, "0")}</span>
							<p className="text-xs leading-relaxed">{step}</p>
						</div>
					))}
				</div>
				<p>
					You can remove a member or change their role at any time from the
					Members page.
				</p>
			</div>

			<div
				className="flex items-center justify-between text-sm pt-4"
				style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}
			>
				<Link
					to="/help/quick-start"
					className="flex items-center gap-1.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "var(--km-on-surface-muted)", textDecoration: "none" }}
					onMouseEnter={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)"; (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
					onMouseLeave={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)"; (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
					onFocus={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface)"; (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
					onBlur={e => { (e.currentTarget as HTMLElement).style.color = "var(--km-on-surface-muted)"; (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
				>
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_back</span>
					Quick start
				</Link>
				<Link
					to="/help/roles"
					className="flex items-center gap-1.5 transition-colors font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0072f5]/60"
					style={{ color: "#6B3FF2", textDecoration: "none" }}
					onMouseEnter={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
					onMouseLeave={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
					onFocus={e => { (e.currentTarget as HTMLElement).style.textDecoration = "underline dotted"; }}
					onBlur={e => { (e.currentTarget as HTMLElement).style.textDecoration = "none"; }}
				>
					Roles and permissions
					<span className="material-symbols-outlined" aria-hidden="true" style={{ fontSize: "16px" }}>arrow_forward</span>
				</Link>
			</div>
		</article>
	);
}
